import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import './V3Login.css'

const API_BASE = 'https://backend.alephmedia.ro/api'

// Pagina de retur din login-ul cu Aleph ID (Keycloak).
// Backend-ul redirectioneaza aici cu:
//   ?code=<cod>            -> schimbam codul pe tokenul Passport (o singura folosire, 60s)
//   ?link=1&hint=&lcode=   -> emailul exista dar nu e verificat: cerem un login clasic
//                             o singura data, apoi legam contul (POST /auth/keycloak/link)
//   ?error=<motiv>         -> afisam eroarea
function KeycloakAuth() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [password, setPassword] = useState('')
  const exchangeStarted = useRef(false)

  const code = searchParams.get('code')
  const isLink = searchParams.get('link') === '1'
  const hint = searchParams.get('hint') || ''
  const lcode = searchParams.get('lcode')
  const kcError = searchParams.get('error')

  const storeSession = (userData) => {
    if (userData.token) {
      localStorage.setItem('authToken', userData.token)
    }

    const userInfo = {
      name: userData.name,
      email: userData.email,
      first_name: userData.first_name || userData.name?.split(' ')[0] || '',
      last_name: userData.last_name || '',
      subscription_type: userData.subscription_type,
      subscription_status: userData.subscription_status,
      subscription_expires_at: userData.subscription_expires_at,
      subscription_kind: userData.subscription_kind
    }

    localStorage.setItem('userData', JSON.stringify(userInfo))

    if (userData.subscription_type) {
      localStorage.setItem('subscriptionLevel', userData.subscription_type || 'free')
    }

    localStorage.removeItem('v3_guest_timeout')
    localStorage.removeItem('v4_guest_timeout')

    // Interfata curenta pentru vizitatori e v4
    navigate('/v4.0')
    window.location.reload()
  }

  useEffect(() => {
    if (!code || exchangeStarted.current) return
    exchangeStarted.current = true

    const doExchange = async () => {
      setLoading(true)
      try {
        const response = await fetch(`${API_BASE}/auth/keycloak/exchange`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ code })
        })

        const data = await response.json()

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Autentificare eșuată')
        }

        storeSession(data.data || data)
      } catch (err) {
        setError(err.message || 'A apărut o eroare. Te rugăm să încerci din nou.')
      } finally {
        setLoading(false)
      }
    }

    doExchange()
  }, [code])

  // Legare explicită: login clasic cu emailul existent, apoi legăm Aleph ID-ul
  const handleLinkSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const loginResponse = await fetch(`${API_BASE}/v3/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: hint, password })
      })

      const loginData = await loginResponse.json()

      if (!loginResponse.ok || !loginData.success) {
        throw new Error(loginData.message || 'Autentificare eșuată')
      }

      const userData = loginData.data || loginData

      const linkResponse = await fetch(`${API_BASE}/auth/keycloak/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        },
        body: JSON.stringify({ lcode })
      })

      const linkData = await linkResponse.json()

      if (!linkResponse.ok || !linkData.success) {
        throw new Error(linkData.message || 'Legarea contului a eșuat')
      }

      storeSession(userData)
    } catch (err) {
      setError(err.message || 'A apărut o eroare. Te rugăm să încerci din nou.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="v3-login-root">
      <div className="v3-login-container">
        <div className="v3-login-header">
          <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" className="v3-login-logo" />
          <h1>Aleph ID</h1>
          <p>
            {isLink
              ? 'Confirmă contul tău Aleph pentru a-l lega de Aleph ID'
              : 'Se finalizează autentificarea...'}
          </p>
        </div>

        {error && (
          <div className="v3-login-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        {kcError && !error && (
          <div className="v3-login-error">
            {kcError === 'email_unverified'
              ? 'Emailul contului tău Aleph ID nu este verificat. Verifică emailul în Aleph ID sau creează un cont clasic.'
              : 'Autentificarea cu Aleph ID a eșuat. Te rugăm să încerci din nou.'}
          </div>
        )}

        {isLink && (
          <form onSubmit={handleLinkSubmit} className="v3-login-form">
            <div className="v3-login-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={hint} disabled readOnly />
            </div>

            <div className="v3-login-field">
              <label htmlFor="password">Parola contului Aleph existent</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>

            <div className="v3-login-buttons">
              <button type="submit" className="v3-login-submit" disabled={loading}>
                {loading ? 'Se leagă contul...' : 'Leagă contul de Aleph ID'}
              </button>
            </div>
          </form>
        )}

        {(error || kcError) && (
          <div className="v3-login-links">
            <Link to="/v3.0/login" className="v3-login-link">
              Înapoi la autentificare
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default KeycloakAuth
